import {addAsset, addAssets, CameraData, OrthoData} from "./redux/assets-slice";
import {addFolder} from './redux/folders-slice';
import {app, dialog, fsPromises, glob} from "./electron-modules";
import path from 'path';
import slash from 'slash';
import {hideBackdrop, showBackdrop} from "./app";
import {PythonExecutable} from "./executable";
import LocalScene from "./viewer/js/projections";

export const lasFileFormat = "las";
export const encompassFileFormat = "idx";
export const imageListFormats = ['csv', 'txt'];
export const planarListFormats = ['plnr'];
export const orthoFormats = ['tif', 'tiff'];

const pointCloudFileFormats = [lasFileFormat, encompassFileFormat];

export const pointCloudFilter = [{name: 'Point Clouds', extensions: pointCloudFileFormats}];
export const imageOrthoFilter = [{name: 'Orthomosaic File', extensions: orthoFormats}];
export const imageCSVFilter = [{name: 'Image CSV', extensions: imageListFormats}];
export const imagePlanarFilter = [{name: 'Planar Image File', extensions: planarListFormats}];

interface ImageDetectResults {
  readonly pathFound: boolean;
  readonly imagePath: string;
  readonly imageExt: string;
  readonly hasImageExt: boolean;
}

export const isAssetFile = (filename) => {
  return isPanoramicFile(filename)
    || isPlanarFile(filename)
    || isPointCloudFile(filename)
    || isOrthomosaicFile(filename);
};

export const isPanoramicFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return imageListFormats.indexOf(extension) !== -1;
};

export const isPlanarFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return planarListFormats.indexOf(extension) !== -1;
};

export const isPointCloudFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return pointCloudFileFormats.indexOf(extension) !== -1;
};

export const isOrthomosaicFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return orthoFormats.indexOf(extension) !== -1;
};

export const addAssetFolder = async (showMessage, dispatch, paths, folderID = null) => {
  const cloudPaths = paths.filter(file => isPointCloudFile(file));
  const panoramicPaths = paths.filter(file => isPanoramicFile(file));
  const orthoPaths = paths.filter(file => isOrthomosaicFile(file));
  const planarPaths = paths.filter(file => isPlanarFile(file));

  const numClouds = cloudPaths.length;
  const numImageFiles = panoramicPaths.length
   + orthoPaths.length
   + planarPaths.length;

  // Nothing to add, exit early
  if (numClouds === 0 && numImageFiles === 0) {
    return;
  }

  if (!folderID) {
    // No folder has been provided, so a new folder is created instead
    const action = dispatch(addFolder("New Folder"));
    folderID = action.payload.id;
  }

  // Add all panoramic data
  const planarFilesAdded = await addPlanarFiles(showMessage,
    dispatch, planarPaths, folderID);

  // Add all planar data
  const panoramicFilesAdded = await addCameraFiles(showMessage,
    dispatch, panoramicPaths, folderID);

  // Add all pointclouds
  const pointsAdded = addPointCloudFiles(dispatch, cloudPaths, folderID);

  // Add orthomosaic data
  const orthosAdded = await addOrthoFiles(showMessage, dispatch,
    orthoPaths, folderID);

  const totalAdded = pointsAdded
    + panoramicFilesAdded
    + planarFilesAdded
    + orthosAdded;

  if (totalAdded === 0) {
    return;
  }

  let addedPlural = totalAdded === 1 ? "" : "s";
  showMessage(`Added [${totalAdded}] file${addedPlural} to project`, {
    variant: "success"
  });
};

export const addPointCloudFiles = (dispatch, assetPaths, folderID) => {
  let assetList = assetPaths.map(pointPath => {
    return {folderID, assetPath: pointPath};
  });

  dispatch(addAssets(assetList));
  return assetPaths.length;
};

export const addOrthoFiles = async (showMessage, dispatch, assetPaths, folderID) => {
  showBackdrop();

  let numAdded = await addOrthoBatch(showMessage, dispatch, assetPaths, folderID);

  hideBackdrop();

  return numAdded;
};

export const addCameraFiles = async (showMessage, dispatch, assetPaths, folderID) => {
  showBackdrop();

  let numAdded = 0;

  for (const assetPath of assetPaths) {
    const added = await addCameraFile(dispatch, assetPath, folderID);

    if (added) {
      numAdded += 1;
    } else {
      showMessage(`Unable to parse ${path.basename(assetPath)}. Please ensure that the CSV matches one of the supported formats listed in the user guide, or that it contains the following columns: Filename, X, Y, Z, Roll, Pitch, Yaw.`, {
        variant: "error",
        autoHideDuration: 10000,
      });
    }
  }

  hideBackdrop();

  return numAdded;
};

export const addPlanarFiles = async (showMessage, dispatch, assetPaths, folderID) => {
  showBackdrop();

  let numAdded = 0;
  for (const assetPath of assetPaths) {
    const added = await addPlanarFile(dispatch, assetPath, folderID);
    if (added) {
      numAdded += 1;
    } else {
      showMessage(`Error loading planar camera file: ${path.basename(assetPath)}`, {
        variant: "error"
      });
    }
  }

  hideBackdrop();

  return numAdded;
};

export const addOrthoBatch = async (showMessage, dispatch, assetPaths, folderID): Promise<number> => {
  if (assetPaths.length === 0) {
    return 0;
  }

  return new Promise(async resolve => {
    const exe = new PythonExecutable();

    let response;
    let commands = [
      "-p", "convert_geotiff",
      "--image_paths", assetPaths,
    ];

    let numAdded = 0;

    exe.run({
      command: commands,
      onLine: jsonData => {
        if (jsonData) {
          response = jsonData;
        }
      },
      onClose: () => {
        if (!response) {
          response = [];
        }

        let assetsList = [];
        response.forEach(geotiff => {
          const assetPath = slash(geotiff.file_path);

          if (geotiff.error) {
            showMessage(`${geotiff.error}: ${path.basename(assetPath)}`, {
              variant: "error"
            });
            return;
          }

          const {corners, base64, width, height,
            scale_x, scale_y, width_original, height_original} = geotiff;

          const data = {
            imageBounds: corners,
            imageData: base64,
            imageSize: [width, height],
            imageSizeOriginal: [width_original, height_original],
            imagePath: assetPath,
            scaleX: scale_x,
            scaleY: scale_y,
          } as OrthoData;

          assetsList.push({folderID, assetPath, data});
          numAdded += 1;
        });

        dispatch(addAssets(assetsList));
        resolve(numAdded);
      }
    });
  });
};

const addPlanarFile = (dispatch, assetPath, folderID): Promise<any> => {
  return new Promise(async resolve => {
    const text = await fsPromises.readFile(assetPath, {encoding: 'utf8'});

    let config;
    let cameras;

    try {
      let decoded = JSON.parse(text);
      config = decoded.config;
      cameras = decoded.data;
    } catch {
      resolve(false);
      return;
    }

    const numCameras = cameras.length;
    if (numCameras === 0) {
      resolve(false);
      return;
    }

    let firstCameraName = cameras[0][0];
    let {pathFound, imagePath, imageExt, hasImageExt}
      = await detectImagePath(assetPath, firstCameraName);

    if (!pathFound) {
      resolve([]);
      return;
    }

    let cameraData: CameraData[] = [];

    for (let i=0;i<numCameras;i++) {
      let camera = cameras[i];

      let filename = camera[0];
      let filePath = buildFilePath(imagePath, filename,
        imageExt, hasImageExt);

      cameraData.push({
        path: filePath,
        name: path.basename(filePath),
        x: camera[1],
        y: camera[2],
        z: camera[3],
        roll: -1*camera[4],
        pitch: -1*camera[5],
        yaw: -1*camera[6],
        config: config
      });
    }

    dispatch(addAsset(folderID, assetPath, cameraData));
    resolve(true);
  });
};

const addCameraFile = (dispatch, assetPath, folderID): Promise<any> => {
  return new Promise(resolve => {
    const userPath = app.getPath('userData');
    const exe = new PythonExecutable();

    let cameraData;
    const inputPath = assetPath;
    const outputPath = path.join(userPath, "inputCsvTemp.csv");
    const projection = LocalScene.engineStyleProjection(LocalScene.dataProjection);

    let commands = [
      "-p", "convert_encompass_csv",
      "--input_file", inputPath,
      "--output_file", outputPath,
      "--projection", projection
    ];

    exe.run({
      command: commands,
      onLine: jsonData => {
        cameraData = jsonData;
      },
      onClose: async () => {
        if (!cameraData) {
          resolve(false);
          return;
        }

        const numCameras = cameraData.length;
        if (numCameras === 0) {
          resolve(false);
          return;
        }

        let firstImageName = cameraData[0].name;
        let {pathFound, imagePath, imageExt, hasImageExt}
          = await detectImagePath(assetPath, firstImageName);

        if (!pathFound) {
          resolve(false);
          return;
        }

        // Update final camera data path / filenames
        for (let i=0;i<numCameras;i++) {
          let filename = cameraData[i].name;
          let filePath = buildFilePath(imagePath, filename,
            imageExt, hasImageExt);

          cameraData[i] = {
            ...cameraData[i],
            path: filePath,
            name: path.basename(filePath)
          };
        }

        dispatch(addAsset(folderID, assetPath, cameraData));
        resolve(true);
      }
    });
  });
};

export const openDialogMulti = (filter): Promise<string[]> => {
  return dialog.showOpenDialog( {
    filters: filter,
    properties: ['openFile', 'multiSelections']
  }).then(result => {
    if (result.canceled) {
      return [];
    }

    const filePaths = result.filePaths.map(path => slash(path));
    return filePaths;
  }).catch(() => {
    dialog.showErrorBox('Error', 'An error occurred while loading the specified file.');
    return [];
  });
};

export const openDialogFile = (filter): Promise<string> => {
  return dialog.showOpenDialog( {
    filters: filter,
    properties: ['openFile']
  }).then(result => {
    if (result.canceled) {
      return null;
    }

    const filePath = slash(result.filePaths[0]);
    return filePath;
  }).catch(() => {
    dialog.showErrorBox('Error', 'An error occurred while loading the specified file.');
    return null;
  });
};

export const openDialogFolder = () => {
  return dialog.showOpenDialog({
    properties: ['openDirectory']
  }).then(result => {
    if (result.canceled) {
      return {
        directoryPath: '',
        assetPaths: Promise.resolve([])
      };
    }

    const filePath = slash(result.filePaths[0]);

    return {
      directoryPath: filePath,
      assetPaths: parseDirectory(filePath)
    };
  }).catch(() => {
    dialog.showErrorBox('Error', 'An error occurred while loading the specified file.');
    return null;
  });
};

export const exportDialogCSV = (filename): Promise<string> => {
  return dialog.showSaveDialog( {
    defaultPath: `${filename}.csv`,
    filters: imageCSVFilter
  }).then(result => {
    if (result.canceled) {
      return;
    }

    const savePath = slash(result.filePath);
    return savePath;
  }).catch(() => {
    dialog.showErrorBox('Error', 'An error occurred while exporting the specified file.');
    return null;
  });
};

export const openDirectory = (): Promise<any> => {
  return dialog.showOpenDialog({
    properties: ["openDirectory"]
  }).then(result => {
    if (result.canceled) {
      return null;
    }
    if (result.filePaths) {
      const outputFolder = slash(result.filePaths[0]);
      return outputFolder;
    }

    return null;
  });
};

const parseDirectory = async (directoryPath: string): Promise<string[]> => {
  const entries = await fsPromises.readdir(directoryPath, {withFileTypes: true});

  const files = entries
    .filter(entry => entry.isFile())
    .map(file => path.join(directoryPath, file.name));

  const directories = entries
    .filter(entry => entry.isDirectory())
    .map(directory => path.join(directoryPath, directory.name));

  for (let folder of directories) {
    files.push(...await parseDirectory(folder));
  }

  return files;
};

const checkImagePath = (directory, filename, suffix, depth = 3) => {
  const results = {
    success: false,
    duplicate: false,
    imagePath: null,
    imageExt: null
  };

  let dirSeperator = "";
  for (var i = 0; i < depth; i++) {
    if (i > 0) {
      dirSeperator += "/*";
    }

    const globPath = `${dirSeperator}/${filename}${suffix}`;
    const paths = glob.sync(globPath, {
      cwd: directory,
      root: directory
    });

    if (paths.length === 1) {
      results.success = true;
      results.imagePath = path.dirname(paths[0]);
      results.imageExt = path.extname(paths[0]);
      return results;
    } else if (paths.length >= 2) {
      results.duplicate = true;
    }
  }

  return results;
};

export const buildFilePath = (imagePath, filename, imageExt, hasImageExt) => {
  let filePath = hasImageExt ?
    path.join(imagePath, filename) :
    path.join(imagePath, `${filename}${imageExt}`);

  // Fix for windows UNC paths
  const isNetworkPath = imagePath.indexOf("//") === 0;
  if (isNetworkPath) {
    filePath = `/${filePath}`;
  }

  return filePath;
};

export const detectImagePath = async (assetPath: string, testImagePath: string): Promise<ImageDetectResults> => {
  const directory = path.dirname(assetPath);
  const testImageExt = path.extname(testImagePath).toLowerCase();
  const hasImageExt = [".jpg", ".png", ".jpeg"].includes(testImageExt);
  const suffix = hasImageExt ? "" : ".{png,jpg,jpeg}";

  let response = {
    pathFound: false,
    imagePath: null,
    imageExt: null,
    hasImageExt
  };

  // Search for image directory using csv path
  let {success, duplicate, imagePath, imageExt}
    = checkImagePath(directory, testImagePath, suffix);

  // Update response values
  response.pathFound = success;
  response.imagePath = imagePath;
  response.imageExt = imageExt;

  console.log("Detected image path: ", imagePath);

  if (!response.pathFound) {
    if (duplicate) {
      dialog.showErrorBox(assetPath, "Multiple image paths found. Image directory must be selected manually");
    } else {
      dialog.showErrorBox(assetPath, "Image path not found. Image directory must be selected manually");
    }

    const results = dialog.showOpenDialogSync({
      title: "Select Image Path",
      properties: ['openDirectory']
    });

    if (results) {
      // Search for image directory using selected folder
      imagePath = slash(results[0]);
      let manual = checkImagePath(imagePath, testImagePath, suffix, 1);

      // Update response values
      response.pathFound = manual.success;
      response.imagePath = imagePath;
      response.imageExt = manual.imageExt;
    }
  }

  return response;
};
